import { z } from "zod/mini";

/** Accept a string wire enum without letting an added producer value reject the whole object. */
function openEnum<const Value extends string>(values: readonly Value[]) {
	const known = new Set<string>(values);
	return z.pipe(
		z.string(),
		z.transform((value: string): Value | "unknown" => (known.has(value) ? (value as Value) : "unknown")),
	);
}

/** Transmission gear, mirrored from the truck's `drive::Gear`. */
export const GearSchema = z.enum(["neutral", "forward", "reverse"]);
export type Gear = z.infer<typeof GearSchema>;

/** The drive state the truck is currently honoring. */
export const DriveStateSchema = z.object({
	steering: z.number(),
	throttle: z.number(),
	brake: z.number(),
	gear: GearSchema,
});
export type DriveState = z.infer<typeof DriveStateSchema>;

/**
 * Where a truck is, in CARLA world metres: x east, y south, yaw clockwise from
 * +x, speed in m/s. Deliberately not latitude and longitude - see
 * `carla/carla_truck/roadmap.py`.
 */
export const PoseSchema = z.object({
	x: z.number(),
	y: z.number(),
	yaw: z.number(),
	speed: z.number(),
});
export type Pose = z.infer<typeof PoseSchema>;

/**
 * How the truck actually responded, in its own frame: acceleration in m/s²
 * (longitudinal positive forward, lateral positive right) and yaw rate in
 * deg/s. Lateral acceleration is the rollover number on a loaded haul truck.
 */
export const MotionSchema = z.object({
	yaw_rate: z.number(),
	accel_lon: z.number(),
	accel_lat: z.number(),
});
export type Motion = z.infer<typeof MotionSchema>;

/**
 * The truck's most recent contact.
 *
 * `at` is unix milliseconds at the instant contact began. An absolute instant
 * rather than an age, so a parked truck's snapshot stays unchanged - and so a
 * viewer who opens the page long after the impact ages it correctly instead of
 * treating the retained snapshot as a fresh crash.
 */
export const CollisionSchema = z.object({
	at: z.number(),
	other: z.string(),
	impulse: z.number(),
});
export type Collision = z.infer<typeof CollisionSchema>;

/** A camera receive/decode/render latency component echoed by the truck. */
export const LatencyEntrySchema = z.object({
	label: z.string(),
	ms: z.number(),
});

/** Per-update telemetry the truck publishes on the `status` track. */
export const TruckStatusSchema = z.object({
	accepted: DriveStateSchema,
	mode: openEnum(["render-only", "simulated"]),
	// Optional across mixed producer/player deployments. Capabilities gate UI
	// actions whose wire commands an older producer would ignore.
	capabilities: z.optional(z.array(z.string())),
	// CARLA added this after the Rust truck wire existed, so mixed versions and
	// the real-truck publisher must keep parsing during a rollout.
	autonomy: z._default(openEnum(["autonomous", "manual"]), "unknown"),
	estopped: z.boolean(),
	// Optional across mixed truck/player deployments.
	estop_by: z.optional(z.string()),
	driver: z.optional(z.string()),
	sequence: z.optional(z.number()),
	media_pipeline: z.record(z.string(), z.array(LatencyEntrySchema)),
	location: z.optional(z.string()),
	// Only a simulated truck knows where it is. A producer that sends neither
	// still renders; the player parks it beside the map instead of on it.
	map: z.optional(z.string()),
	pose: z.optional(PoseSchema),
	// Measured feedback, present only from a producer with a vehicle underneath
	// it. `accepted` is the command the gate let through; these are what the
	// vehicle did with it, and arrive whoever is driving. The Rust truck has no
	// vehicle bus and so sends none of them.
	measured: z.optional(DriveStateSchema),
	motion: z.optional(MotionSchema),
	collision: z.optional(CollisionSchema),
});
export type TruckStatus = z.infer<typeof TruckStatusSchema>;
